import { Signal } from 'signal-polyfill';

import { adminApi } from '../../../api/admin.api';
import type { OrganizationListItemDto } from '../../../api/types';
import { navigate } from '../../../router';
import { authState, canAccessAppAdminConfiguration } from '../../../state/auth.state';
import { BaseElement } from '../../base-element';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-spinner';
import './admin-config-shell';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';

export class AdminSportCodesPage extends BaseElement {
  private organizations = new Signal.State<OrganizationListItemDto[]>([]);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private forbidden = new Signal.State(false);
  private initialized = false;

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();

    if (!auth.isAuthenticated || !canAccessAppAdminConfiguration.get() || this.forbidden.get()) {
      return 'forbidden';
    }

    if (this.loading.get()) {
      return 'loading';
    }

    if (this.error.get()) {
      return 'error';
    }

    if (this.organizations.get().length === 0) {
      return 'empty';
    }

    return 'loaded';
  });

  connectedCallback(): void {
    this.watch(this.organizations, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.forbidden, () => this.updateRender());
    this.watch(authState, () => this.ensureLoaded());
    this.watch(canAccessAppAdminConfiguration, () => this.ensureLoaded());

    this.updateRender();
    this.ensureLoaded();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canAccessAppAdminConfiguration.get();
  }

  private ensureLoaded(): void {
    if (!this.canAccess() || this.initialized) {
      return;
    }

    this.initialized = true;
    void this.loadOrganizations();
  }

  private async loadOrganizations(): Promise<void> {
    if (!this.canAccess()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.forbidden.set(false);

    const response = await adminApi.organizations.list();
    if (response.error) {
      this.organizations.set([]);
      this.loading.set(false);

      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.error.set(response.error.title || 'Unable to load organizations for sport-code management.');
      return;
    }

    this.organizations.set(response.data ?? []);
    this.loading.set(false);
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) {
      return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private handleRetry = (): void => {
    void this.loadOrganizations();
  };

  private handleOpenOrganizations = (): void => {
    navigate('/admin/configuration/organizations');
  };

  private handleManageSportCodes = (organizationId: string): void => {
    navigate(`/admin/configuration/organizations/${organizationId}/sport-codes`);
  };

  private renderLoading(): string {
    return `
      <div class="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[24px] border border-neutral-200 bg-white" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading organizations for sport-code management...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="rounded-[24px] border border-error-200 bg-error-50 p-6" data-testid="error-state">
        <h2 class="text-lg font-semibold text-error-700">Unable to load sport-code organizations</h2>
        <p class="mt-2 text-sm text-error-600">${this.esc(message)}</p>
        <dt-button class="mt-4" variant="outline" data-testid="retry-btn">Retry</dt-button>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="rounded-[24px] border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="empty-state">
        <div class="text-lg font-semibold text-neutral-900">No organizations available yet</div>
        <p class="mt-2 text-sm text-neutral-600">Create an organization first, then manage its sport codes from this configuration area.</p>
        <dt-button class="mt-5" data-testid="open-organizations-btn">Go to organizations</dt-button>
      </div>
    `;
  }

  private renderTable(): string {
    return `
      <dt-card variant="outlined">
        <div slot="header">Choose an organization</div>
        <div class="flex flex-col gap-4">
          <div class="max-w-2xl text-sm text-neutral-600">
            Sport codes stay organization-scoped. Pick an organization to manage its sport-code catalog and documentation links.
          </div>

          <div class="overflow-hidden rounded-[24px] border border-neutral-200 bg-white" data-testid="organizations-table">
            <div class="grid grid-cols-1 gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 lg:grid-cols-[0.8fr_1.6fr_1.4fr_0.9fr_1.1fr] lg:gap-4">
              <div>Code</div>
              <div>Name</div>
              <div>Website</div>
              <div>Created</div>
              <div>Actions</div>
            </div>

            <div class="divide-y divide-neutral-200">
              ${this.organizations
                .get()
                .map(
                  (organization) => `
                    <div class="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[0.8fr_1.6fr_1.4fr_0.9fr_1.1fr] lg:items-center" data-testid="organization-row" data-organization-id="${this.esc(organization.organizationId)}">
                      <div class="text-sm font-semibold text-neutral-900">${this.esc(organization.code)}</div>
                      <div class="text-sm text-neutral-700">${this.esc(organization.name)}</div>
                      <div class="text-sm text-neutral-700">
                        ${organization.websiteUrl ? `<a href="${this.esc(organization.websiteUrl)}" class="text-primary-600 underline" target="_blank" rel="noreferrer">${this.esc(organization.websiteUrl)}</a>` : '<span class="text-neutral-400">—</span>'}
                      </div>
                      <div class="text-sm text-neutral-600">${this.esc(this.formatTimestamp(organization.createdAtUtc))}</div>
                      <div>
                        <button class="rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50" data-testid="manage-sport-codes-btn" data-organization-id="${this.esc(organization.organizationId)}">Manage sport codes</button>
                      </div>
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        </div>
      </dt-card>
    `;
  }

  private renderPageBody(): string {
    const state = this.pageState.get();
    if (state === 'loading') {
      return this.renderLoading();
    }

    if (state === 'error') {
      return this.renderError(this.error.get() ?? 'Unable to load sport-code organizations.');
    }

    if (state === 'empty') {
      return this.renderEmpty();
    }

    return this.renderTable();
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">Only AppAdmin users can access the configuration area.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    if (this.pageState.get() === 'forbidden') {
      this.render(this.renderForbidden());
      return;
    }

    this.render(`
      <dt-admin-config-shell
        active-section="sport-codes"
        section-title="Sport Codes"
        section-description="Choose an organization, then manage its organization-scoped sport codes and optional documentation links."
        sport-codes-path="/admin/configuration/sport-codes"
      >
        ${this.renderPageBody()}
      </dt-admin-config-shell>
    `);

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const openOrganizationsBtn = this.$('[data-testid="open-organizations-btn"]');
    if (openOrganizationsBtn) {
      openOrganizationsBtn.addEventListener('click', this.handleOpenOrganizations);
    }

    this.$$('[data-testid="manage-sport-codes-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const organizationId = button.getAttribute('data-organization-id');
        if (organizationId) {
          this.handleManageSportCodes(organizationId);
        }
      });
    });
  }
}

customElements.define('dt-admin-sport-codes-page', AdminSportCodesPage);